import { Suspense, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import appConfig from "../configs/app.config";
import ProtectedRoute from "../components/route/ProtectedRoute";
import PublicRoute from "../components/route/PublicRoute";
import {
  authRoutes,
  protectedRoutes,
  publicRoutes,
} from "../configs/routes.config";
import AppRoute from "../components/route/AppRoute";
import { useSelector } from "react-redux";
import { hexToRgba, ensureReadableAccent } from "../utils/colorFormat";
import AuthRoute from "../components/route/AuthRoute";
import Spinner from "../components/ui/Spinner";
const { authenticatedEntryPath } = appConfig;

const AllRoutes = () => {
  return (
    <Routes>
      <Route path="/" element={<ProtectedRoute />}>
        <Route
          path="/"
          element={<Navigate replace to={authenticatedEntryPath} />}
        />
        {protectedRoutes.map((route, index) => (
          <Route
            key={route.key + index}
            path={route.path}
            element={
              <AppRoute
                routeKey={route.key}
                component={route.component}
                {...route.meta}
              />
            }
          />
        ))}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
      <Route path="/" element={<PublicRoute />}>
        {publicRoutes.map((route) => (
          <Route
            key={route.path}
            path={route.path}
            element={<AppRoute component={route.component} {...route.meta} />}
          />
        ))}
      </Route>
      <Route path="/" element={<AuthRoute />}>
        {authRoutes.map((route) => (
          <Route
            key={route.path}
            path={route.path}
            element={<AppRoute component={route.component} {...route.meta} />}
          />
        ))}
      </Route>
    </Routes>
  );
};

const Views = () => {
  const currentStore = useSelector(
    (state) => state?.base?.common?.currentStore
  );
  const sitecolor =
    useSelector((state) => state?.base?.common?.currentStore?.sitecolor) ||
    "#0A1034";
  useEffect(() => {
    const root = document.documentElement;
    // Legacy variables (keep for existing components)
    root.style.setProperty("--site-color", hexToRgba(sitecolor, 1));
    root.style.setProperty("--site-color-10", hexToRgba(sitecolor, 0.1));

    // New design system accent tokens. Light brand colors get pulled to a
    // darker lightness so a white foreground always has readable contrast
    // on solid accent fills (buttons, badges, primary CTAs).
    const safeAccent = ensureReadableAccent(sitecolor);
    root.style.setProperty("--accent", safeAccent);
    root.style.setProperty("--accent-fg", "#FFFFFF");
    root.style.setProperty("--accent-soft", hexToRgba(safeAccent, 0.08));
    root.style.setProperty("--accent-border", hexToRgba(safeAccent, 0.22));
  }, [sitecolor]);

  useEffect(() => {
    if (currentStore) {
      document.title = "Thisway - " + (currentStore.name || "");
    } else {
      document.title = "Thisway";
    }
  }, [currentStore]);

  return (
    // A real fallback, not an empty one: a route chunk that is slow (or that
    // stays pending because `lazyRoute` swallowed an `undefined` module while
    // chunkReloadGuard reloads) must show a spinner, never a blank page.
    <Suspense
      fallback={
        <div className="flex items-center justify-center min-h-[60vh]">
          <Spinner size={40} />
        </div>
      }
    >
      <AllRoutes />
    </Suspense>
  );
};

export default Views;
